import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Input } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { Link } from 'react-router-dom';
import { getReferralsAdmin, setReferralStatus, setReferralCreditIssued, saveReferralConfig, type ReferralDto } from '@/data/client';
import { adminClientPath } from './data';
import { apiErrMsg } from './util';

/* Every referral across clients, the actions that move one along, the credit
   "issued on" confirmation, and the amounts (global default + per-account
   overrides). */
const STATUSES: { value: ReferralDto['status']; label: string }[] = [
  { value: 'received', label: 'Received' }, { value: 'contacted', label: 'Contacted' }, { value: 'qualified', label: 'Qualified — credit earned' }, { value: 'not_fit', label: 'Not a fit' }, { value: 'existing_client', label: 'Already a client' },
];
const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

export function ReferralsBoard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'referrals'], queryFn: getReferralsAdmin });
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'referrals'] });
  const rows = (data?.referrals ?? []).filter((r) => filter === 'all' || r.status === 'received' || r.status === 'contacted' || (r.status === 'qualified' && !r.creditIssuedOn));
  return (
    <div className="dash-stack">
      <div>
        <h2>Referral Rewards</h2>
        <p>Businesses our clients referred. A referral qualifies when it is in our industries, knew we were calling, and agreed to a meeting; marking it qualified fixes the credit at that account&rsquo;s rate and emails the person who sent it. &ldquo;Credit issued on&rdquo; is our confirmation the refund went out — the client sees it too.</p>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button size="sm" variant={filter === 'open' ? 'primary' : 'outline'} onClick={() => setFilter('open')}>Needs action</Button>
        <Button size="sm" variant={filter === 'all' ? 'primary' : 'outline'} onClick={() => setFilter('all')}>All</Button>
      </div>
      {isLoading ? <p className="dash-muted">Loading…</p> : rows.length === 0 ? <p className="dash-muted">{filter === 'open' ? 'Nothing needs action.' : 'No referrals yet.'}</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table className="dash-table">
            <thead><tr><th>Business</th><th>Referred by</th><th>Sent</th><th>Status</th><th>Credit</th></tr></thead>
            <tbody>{rows.map((r) => <Row key={r.id} r={r} onChanged={refresh} />)}</tbody>
          </table>
        </div>
      )}
      {data && <ConfigPanel config={data.config} onChanged={refresh} />}
    </div>
  );
}

function Row({ r, onChanged }: { r: ReferralDto; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [date, setDate] = useState(r.creditIssuedOn ?? new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState(r.creditIssuedNote ?? '');
  const run = async (fn: () => Promise<unknown>) => { setBusy(true); setMsg(null); try { await fn(); onChanged(); } catch (e) { setMsg(apiErrMsg(e, 'Failed.')); } finally { setBusy(false); } };
  return (
    <tr>
      <td>
        <strong>{r.business}</strong>
        <div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>
          {[r.contactName, r.phone, r.email].filter(Boolean).join(' · ')}{r.website && <> · <a href={r.website} target="_blank" rel="noreferrer">{r.website.replace(/^https?:\/\//, '')}</a></>}
        </div>
        {r.notes && <div className="dash-muted" style={{ fontSize: 'var(--text-xs)', maxWidth: 360 }}>{r.notes}</div>}
      </td>
      <td><strong>{r.clientName || '—'}</strong><div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{r.submittedBy}</div></td>
      <td className="dash-muted" style={{ whiteSpace: 'nowrap' }}>{r.submittedAt.slice(0, 10)}</td>
      <td>
        <select className="tr-select" value={r.status} disabled={busy} onChange={(e) => run(() => setReferralStatus(r.id, e.target.value as ReferralDto['status']))}>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        {r.statusAt && <div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{r.statusAt.slice(0, 10)}</div>}
      </td>
      <td style={{ minWidth: 260 }}>
        {r.status !== 'qualified' ? (
          /* A dash here read as "nothing to do". The credit is earned by the
             status change, so say so where the admin is looking for it. */
          <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>
            {r.status === 'not_fit' || r.status === 'existing_client' ? 'No credit for this status.' : 'Set the status to “Qualified — credit earned” to fix the credit at this account’s rate; the issue controls appear here.'}
          </span>
        ) : r.creditIssuedOn ? (
          <div>
            <Badge variant="success" dot>{money(r.creditAmount ?? 0)} issued {r.creditIssuedOn}</Badge>
            {r.creditIssuedNote && <div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{r.creditIssuedNote}</div>}
            <button type="button" className="asst-drawer__link" disabled={busy} onClick={() => run(() => setReferralCreditIssued(r.id, null))}>Undo</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Badge variant="warning">{money(r.creditAmount ?? 0)} earned — not issued</Badge>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input className="tr-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 150 }} />
              <input className="tr-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Invoice / note" style={{ width: 150 }} />
              <Button size="sm" variant="outline" disabled={busy || !date} onClick={() => run(() => setReferralCreditIssued(r.id, date, note))} iconLeft={<Icon name="check" size={13} />}>Credit issued</Button>
            </div>
          </div>
        )}
        {msg && <div className="admin-modal__err" style={{ fontSize: 'var(--text-xs)' }}>{msg}</div>}
      </td>
    </tr>
  );
}

function ConfigPanel({ config, onChanged }: { config: { defaultAmount: number; notify: string[]; overrides: { orgId: string; clientName: string; amount: number }[] }; onChanged: () => void }) {
  const [amount, setAmount] = useState(String(config.defaultAmount));
  const [notify, setNotify] = useState(config.notify.join(', '));
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, ok: string) => { setBusy(true); setMsg(null); try { await fn(); setMsg(ok); onChanged(); } catch (e) { setMsg(apiErrMsg(e, 'Failed.')); } finally { setBusy(false); } };
  return (
    <div style={{ marginTop: 24 }}>
      <h2>Credit amounts &amp; notifications</h2>
      <div className="admin-prof" style={{ maxWidth: 760 }}>
        <Input label="Default credit ($)" value={amount} onChange={(e) => setAmount(e.target.value)} hint="Applies to every account without an override. Fixed on each referral when it is marked qualified." />
        <Input label="Notify on every referral" value={notify} onChange={(e) => setNotify(e.target.value)} hint="Comma-separated. The referring client's account manager is always added." />
        <div style={{ gridColumn: '1 / -1' }}><Button size="sm" disabled={busy} onClick={() => run(() => saveReferralConfig({ defaultAmount: Number(amount), notify: notify.split(',').map((s) => s.trim()).filter(Boolean) }), 'Saved.')}>Save</Button></div>
      </div>
      <h4 className="admin-sect__title" style={{ marginTop: 18 }}>Per-account overrides</h4>
      <p className="admin-hint">Set on each client's <strong>Advanced</strong> tab. Accounts with their own amount:</p>
      {config.overrides.length === 0 ? <p className="dash-muted">None — every account earns the default.</p> : (
        <ul className="conv-gaps" style={{ maxWidth: 560 }}>
          {config.overrides.map((o) => (
            <li key={o.orgId}><Link to={adminClientPath(o.orgId, 'advanced')}>{o.clientName || o.orgId}</Link><strong>{money(o.amount)}</strong></li>
          ))}
        </ul>
      )}
      {msg && <p className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{msg}</p>}
    </div>
  );
}
